import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Bot,
  Calculator,
  DollarSign,
  Loader2,
  Mic,
  MicOff,
  RotateCcw,
  Send,
  Sparkles,
  TrendingUp,
  User,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { type ChatMessage, useWebSocket } from '@/hooks/useWebSocket';
import {
  AudioRecorder,
  isAudioRecordingSupported,
} from '@/lib/audio-recording';
import { trpcClient } from '@/utils/trpc';

const WS_URL =
  import.meta.env.VITE_WS_URL ||
  `ws://localhost:${import.meta.env.VITE_WS_PORT || 8080}`;

export function AiChat() {
  const [inputMessage, setInputMessage] = useState('');
  const [analysisItem, setAnalysisItem] = useState('');
  const [analysisAmount, setAnalysisAmount] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);

  const {
    isConnected,
    isConnecting,
    isTyping,
    messages,
    lastError,
    sendMessage,
    requestAnalysis,
    requestInsights,
    clearMessages,
    connect,
  } = useWebSocket({ url: WS_URL });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() && isConnected) {
      sendMessage(inputMessage);
      setInputMessage('');
    }
  };

  const handleAnalysisRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number.parseFloat(analysisAmount);
    if (analysisItem.trim() && !isNaN(amount) && amount > 0) {
      requestAnalysis(analysisItem, amount);
      setAnalysisItem('');
      setAnalysisAmount('');
    }
  };

  const handleQuickQuestion = (question: string) => {
    if (isConnected) {
      sendMessage(question);
    }
  };

  // Simple audio recording functions
  const startRecording = async () => {
    try {
      if (!audioRecorderRef.current) {
        audioRecorderRef.current = new AudioRecorder();
      }

      await audioRecorderRef.current.startRecording();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Não foi possível acessar o microfone');
    }
  };

  const stopRecording = async () => {
    try {
      if (!audioRecorderRef.current || !isRecording) return;

      setIsRecording(false);
      setIsTranscribing(true);

      const audioBlob = await audioRecorderRef.current.stopRecording();

      const base64Audio = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(audioBlob);
      });

      const result = await trpcClient.audio.transcribe.mutate({
        audioData: base64Audio,
        mimeType: audioBlob.type,
      });

      setIsTranscribing(false);

      if (result.success && result.transcription) {
        setInputMessage(result.transcription);
      } else {
        alert('Não foi possível transcrever o áudio');
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
      setIsRecording(false);
      setIsTranscribing(false);
      alert('Erro ao processar gravação');
    }
  };

  const playTextAsAudio = async (text: string) => {
    try {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      const result = await trpcClient.audio.generateSpeech.mutate({
        text: text.substring(0, 4000), // Limit text length
        voice: 'nova',
        speed: 1.0,
      });

      if (result.success && result.audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(result.audioData), (c) => c.charCodeAt(0))],
          { type: 'audio/mpeg' }
        );

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        setCurrentAudio(audio);
        await audio.play();

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          setCurrentAudio(null);
        };
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      alert('Erro ao reproduzir áudio');
    }
  };

  const renderMessage = (message: ChatMessage) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    return (
      <div
        className={`group relative ${isUser ? 'bg-transparent' : 'bg-gray-800/50'}`}
        key={message.id}
      >
        <div className="mx-auto max-w-4xl px-4 py-6">
          <div className="flex gap-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-sm ${
                  isUser
                    ? 'bg-blue-600 text-white'
                    : isSystem
                      ? 'bg-gray-600 text-white'
                      : 'bg-green-600 text-white'
                }`}
              >
                {isUser ? (
                  <User className="h-4 w-4" />
                ) : isSystem ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </div>
            </div>

            {/* Message Content */}
            <div className="min-w-0 flex-1">
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-gray-100 leading-relaxed">
                  {message.content}
                </div>
              </div>

              {/* Timestamp and Audio Controls - only visible on hover */}
              <div className="mt-2 flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                <span className="text-muted-foreground text-xs">
                  {formatDistanceToNow(message.timestamp, { addSuffix: true })}
                </span>

                {/* Audio playback for AI messages */}
                {!isUser && !isSystem && (
                  <Button
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-gray-200"
                    disabled={!isConnected}
                    onClick={() => playTextAsAudio(message.content)}
                    size="sm"
                    title="Reproduzir como áudio"
                    variant="ghost"
                  >
                    {currentAudio ? (
                      <VolumeX className="h-3 w-3" />
                    ) : (
                      <Volume2 className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const quickQuestions = [
    'Como estou financeiramente este mês?',
    'Quais são minhas maiores categorias de gastos?',
    'Estou no caminho certo com minhas metas?',
    'Como posso economizar mais dinheiro?',
    'Devo criar um orçamento?',
  ];

  return (
    <div className="flex h-full flex-col bg-gray-900">
      {/* Header */}
      <div className="border-gray-700 border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-green-600">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-100 text-lg">
                IA Financeira
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                {isConnecting && <Loader2 className="h-3 w-3 animate-spin" />}
                <span>
                  {isConnecting
                    ? 'Conectando...'
                    : isConnected
                      ? 'Online'
                      : 'Offline'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Quick Actions */}
            <Button
              className="text-gray-600 hover:text-gray-900"
              disabled={!isConnected || isTyping}
              onClick={() => requestInsights()}
              size="sm"
              variant="ghost"
            >
              <TrendingUp className="mr-1 h-4 w-4" />
              Insights
            </Button>

            <Button
              className="text-gray-600 hover:text-gray-900"
              disabled={messages.length === 0}
              onClick={clearMessages}
              size="sm"
              variant="ghost"
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              Limpar
            </Button>
          </div>
        </div>

        {lastError && (
          <div className="mt-3 rounded-lg border border-destructive/20 bg-destructive/10 p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-destructive" />
              <span className="text-destructive/80 text-sm">{lastError}</span>
              <Button
                className="ml-auto"
                onClick={connect}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 && isConnected ? (
          <div className="flex h-full items-center justify-center">
            <div className="mx-auto max-w-md px-4 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
                <Sparkles className="h-8 w-8 text-gray-300" />
              </div>
              <h2 className="mb-2 font-semibold text-gray-100 text-xl">
                Como posso ajudar com suas finanças?
              </h2>
              <p className="mb-6 text-muted-foreground">
                Posso analisar seus gastos, ajudar com orçamentos, verificar
                acessibilidade e fornecer conselhos financeiros personalizados.
              </p>

              {/* Suggested prompts */}
              <div className="space-y-2">
                {quickQuestions.slice(0, 4).map((question, index) => (
                  <button
                    className="block w-full rounded-lg border border-gray-700 p-3 text-left text-gray-300 text-sm transition-colors hover:border-gray-600 hover:bg-gray-800"
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div>
            {messages.map(renderMessage)}

            {isTyping && (
              <div className="bg-gray-800/50">
                <div className="mx-auto max-w-4xl px-4 py-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-green-600 text-white">
                        <Sparkles className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-1">
                        <div className="h-2 w-2 animate-bounce rounded-full bg-gray-300" />
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-gray-300"
                          style={{ animationDelay: '0.1s' }}
                        />
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-gray-300"
                          style={{ animationDelay: '0.2s' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-gray-700 border-t p-4">
        <div className="mx-auto max-w-4xl">
          <form className="relative" onSubmit={handleSendMessage}>
            <Textarea
              className="max-h-32 min-h-[52px] w-full resize-none rounded-xl border-gray-600 bg-gray-800 pr-12 text-gray-100 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              disabled={!isConnected}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder="Mensagem para IA Financeira..."
              value={inputMessage}
            />
            <div className="absolute right-2 bottom-2 flex items-center gap-1">
              {/* Simple Voice Recording Button */}
              {isAudioRecordingSupported() && (
                <Button
                  className={`h-8 w-8 rounded-lg p-0 ${
                    isRecording
                      ? 'animate-pulse bg-red-600 hover:bg-red-700'
                      : ''
                  }`}
                  disabled={!isConnected || isTranscribing}
                  onClick={isRecording ? stopRecording : startRecording}
                  size="sm"
                  type="button"
                >
                  {isTranscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
              )}

              <Button
                className="h-8 w-8 rounded-lg p-0"
                disabled={!isConnected || !inputMessage.trim() || isTyping}
                size="sm"
                type="submit"
              >
                {isTyping ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </form>

          {/* Quick Actions Row */}
          <div className="mt-2 flex items-center gap-2">
            <Button
              className="text-muted-foreground text-xs hover:text-gray-200"
              disabled={!isConnected || isTyping}
              onClick={() => {
                setAnalysisItem('');
                setAnalysisAmount('');
                const item = prompt('O que você quer comprar?');
                const amount = prompt('Quanto custa?');
                if (item && amount && !isNaN(Number(amount))) {
                  requestAnalysis(item, Number(amount));
                }
              }}
              size="sm"
              variant="ghost"
            >
              <Calculator className="mr-1 h-3 w-3" />
              Verificar Acessibilidade
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
